import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {useUpdateEffect} from 'ahooks';
import {Button, Size, Type} from '@Components/button';
import {useGetLinkInputSuggestions} from '@Hooks/poster-editor/useGetInputSuggestions';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import styles from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

interface PosterEditorFancyTextPanelProps {
  panelId: string;
}

export default function PosterEditorFancyTextPanel({...props}: PosterEditorFancyTextPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemText = useGetKeyFromFancyTextItem('text');

  const [hasContentChanged, setHasContentChanged] = useState(false);
  const [fancyText, setFancyText] = useState<string>('');
  const linkSuggestions = useGetLinkInputSuggestions();

  useEffect((): void => {}, [activeItem]);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  if (!activeItem?.isFancyText() || activeItemText === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_text')} />;
  };

  const onApply = (): void => {
    activeItem.updateTextContent(fancyText);
    onClose();
  };

  const getInputValue = (): string => {
    return !hasContentChanged ? activeItemText : fancyText;
  };

  const getInputSelector = (): ReactElement => {
    return (
      <ContextualMenuInputsOption
        id="sticker-interactivity-link-options"
        placeholder={window.i18next.t('pmwjs_link_placeholder_2')}
        value={getInputValue()}
        onInputChange={(val: string): void => {
          setHasContentChanged(true);
          setFancyText(val);
        }}
        onInputChangeEnded={(val: string): void => {
          setFancyText(val);
        }}
        elementTitle={window.i18next.t('pmwjs_link_placeholder_2')}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <div className="spacing-p-t-3 spacing-p-l-6 spacing-p-r-6 spacing-p-b-6">
        <InputSuggestionsDropdown
          dropdownId="fancy-text-content-panel-dropdown"
          inputSelector={getInputSelector()}
          onSuggestionSelect={setFancyText}
          primarySuggestionsList={linkSuggestions}
          popupClassName={styles.popupMobilePanel}
          inputValue={getInputValue()}
        />
        <Button
          customClasses="_full-width spacing-m-t-4"
          size={Size.SMALL}
          type={Type.PRIMARY}
          onClick={onApply}
          disabled={fancyText === '' || fancyText === activeItemText}
          text={window.i18next.t('pmwjs_apply')}
        />{' '}
      </div>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
